// Edge Device types removed - focusing on IQWatch devices only
import api from "./api.ts";

export interface CreateOrgParams {
  name: string;
  description: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
}

export interface CreateOrgResponse {
  message: string;
  data: {
    organization: {
      id: string;
      name: string;
      code: string;
      description: string;
      createdAt: string;
    };
    admin: {
      id: string;
      email: string;
      name: string;
      status: string;
    };
    site: {
      id: string;
      name: string;
      type: string;
      createdAt: string;
    };
    invitation: {
      token: string;
      email: string;
    };
  };
}

/**
 * 创建组织
 */
export const createOrganization = async (
  data: CreateOrgParams
): Promise<CreateOrgResponse> => {
  const response = await api.post("/partner/organizations", data);
  return response.data;
};

/**
 * 更新组织参数 - 不允许修改email
 */
export interface UpdateOrgParams {
  name: string;
  description: string;
  adminFirstName: string;
  adminLastName: string;
}

/**
 * 更新组织
 */
export const updateOrganization = async (
  orgId: string,
  data: UpdateOrgParams
): Promise<CreateOrgResponse> => {
  const response = await api.put(`/partner/organizations/${orgId}`, data);
  return response.data;
};

export interface Org {
  id: string;
  name: string;
  code: string;
  description: string;
  createdAt: string;
  owner: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

/**
 * 获取 partner 下面所有 HOA 组织
 */
export const getPartnerOrgs = async (): Promise<Org[]> => {
  const response = await api.get<Org[]>("/partner/organizations");
  return response.data;
};

// 删除 HOA 组织
export interface DeleteOrgResponse {
  message: string;
  data: {
    id: string;
    name: string;
    code: string;
    deletedAt: string;
    owner: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
    };
  };
}

export const deleteOrganization = async (
  orgId: string
): Promise<{ message: string }> => {
  const response = await api.delete(`/partner/organizations/${orgId}`);
  return response.data;
};

// 站点相关接口
export interface CreateSiteParams {
  parentId?: string;
  site: {
    name: string;
    siteType: string;
  };
  org_id: string;
}

export interface Site {
  id: string;
  name: string;
  type: string;
  detail: any;
  created_at: string;
  updated_at: string;
  parent_id?: string;
  org_id: string;
  lft?: number;
  rgt?: number;
  children?: Site[];
  // edge_devices removed - focusing on IQWatch devices only
  iqwatch_devices?: {
    id: string;
    name: string;
    description: string | null;
    device_type: "IQWATCH";
    iqwatch_device_id: string;
    iqwatch_chip_id: string;
    iqwatch_mac_address: string;
    status: "running" | "stopped" | "error";
    last_sync_at: string;
    last_data_timestamp: string;
    last_soc: string;
    last_battery_voltage: string;
    last_load_power: string;
    last_solar_power: string;
    sync_interval_minutes: number;
    auto_sync_enabled: boolean;
    site: {
      id: string;
      name: string;
    };
    organization: {
      id: string;
      name: string;
    };
    building: string | null;
    apartment: string | null;
    created_at: string;
    updated_at: string;
  }[];
}

export interface Device {
  id: string;
  name: string;
  site_id: string;
  mac_address: string;
  status: "online" | "offline";
  last_seen: string;
  createdAt: string;
  updatedAt: string;
}

// Edge Device API functions removed - focusing on IQWatch devices only

export const createSite = async (data: CreateSiteParams): Promise<Site> => {
  const response = await api.post("/site/site", data);
  return response.data;
};

export interface UpdateSiteParams {
  name: string;
  siteType: string;
  detail?: any;
  parentId?: string;
}

export const updateSite = async (
  siteId: string,
  data: UpdateSiteParams
): Promise<Site> => {
  const response = await api.put(`/site/site/${siteId}`, data);
  return response.data;
};

export const deleteSite = async (
  siteId: string
): Promise<{ message: string }> => {
  const response = await api.delete(`/site/site/${siteId}`);
  return response.data;
};

export interface OrganizationSiteTreeResponse {
  organization: {
    id: string;
    name: string;
    code: string;
  };
  sites: Site[];
  total_sites: number;
  total_iqwatch_devices: number;
}

export const getOrganizationSiteTree = async (
  orgId: string,
  includeDevices: boolean = true,
  includeIQWatch: boolean = true
): Promise<OrganizationSiteTreeResponse> => {
  const params = new URLSearchParams();
  params.append("includeDevices", includeDevices.toString());
  params.append("includeIQWatch", includeIQWatch.toString());

  const response = await api.get(
    `/tree/organization/${orgId}/hierarchy?${params.toString()}`
  );
  return response.data;
};

export interface OrgWithPermission {
  id: string;
  name: string;
  code: string;
  description: string;
  createdAt: string;
  owner: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

/**
 * 获取当前用户有权限访问的组织列表
 */
export const getMyOrganizations = async (): Promise<OrgWithPermission[]> => {
  const response = await api.get<{ data: OrgWithPermission[] }>(
    "/partner/org-perms/my-orgs" // Keep the original endpoint since it now includes owner info
  );
  return response.data.data || []; // Add fallback to empty array
};

/**
 * 获取特定用户的组织权限列表
 */
export interface UserOrgPermissionsResponse {
  data: OrgWithPermission[];
  userId: string;
  partnerId: string;
}

export const getUserOrgPermissions = async (
  userId: string
): Promise<UserOrgPermissionsResponse> => {
  const response = await api.get<UserOrgPermissionsResponse>(
    `/partner/users/${userId}/org-permissions`
  );
  return response.data;
};

// 你可以继续添加 getOrganizations, updateOrganization, deleteOrganization 等接口
